/**
 * Custom Layout
 *
 * Main layout component that wraps the entire application.
 * Provides responsive design with different behaviors for mobile and desktop.
 */

import React from 'react';
import { Layout, LayoutProps } from 'react-admin';
import { useTheme, useMediaQuery, alpha } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { CustomMenu } from './CustomMenu';
import { CustomAppBar } from './CustomAppBar';

export const CustomLayout: React.FC<LayoutProps> = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const isDashboard = location.pathname === '/';

  return (
    <Layout
      {...props}
      appBar={CustomAppBar}
      menu={CustomMenu}
      sx={{
        // Page background
        backgroundColor: theme.palette.background.default,

        // RA sets min-width: fit-content on the root, which makes the whole
        // app grow past the viewport when a list has a wide table. Force it
        // to respect the window width; horizontal scrolling happens inside
        // the datagrid instead.
        minWidth: '0 !important',
        maxWidth: '100vw',
        overflowX: 'hidden',

        // Let the datagrid's Paper wrapper and root shrink below content
        // width so the inner table scroller can clip and scroll.
        '& .RaList-main > .MuiPaper-root': { minWidth: 0 },
        '& .RaDatagrid-root': { minWidth: 0 },

        // Desktop: Fixed height, no scroll
        ...(!isMobile && {
          height: '100vh',
          overflow: 'hidden',
        }),

        // App frame
        '& .RaLayout-appFrame': {
          marginTop: 0,
          alignItems: 'flex-start',
          ...(!isMobile && {
            height: '100%',
          }),
        },

        // Sidebar resets
        '& .RaSidebar-root': {
          height: 'auto',
          margin: '0 !important',
          padding: '0 !important',
        },
        '& .RaSidebar-root .MuiDrawer-docked': {
          height: 'auto',
          margin: '0 !important',
        },
        '& .RaSidebar-root .MuiDrawer-paper': {
          position: 'relative',
          overflow: 'visible',
          border: 'none',
          backgroundColor: 'transparent',
          margin: '0 !important',
          padding: '0 !important',
        },
        '& .RaSidebar-fixed': {
          position: 'relative !important',
          height: 'auto !important',
          overflow: 'visible !important',
          margin: '0 !important',
        },

        // Content with sidebar container
        '& .RaLayout-contentWithSidebar': {
          display: 'flex',
          flex: 1,
          gap: 0,
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          ...(!isMobile && {
            padding: (theme as any).custom?.layout?.desktopContainerPadding || '8px',
            overflow: 'hidden',
          }),
        },
        // Every flex child of the row (incl. the content column) must be
        // allowed to shrink below its content width, otherwise a wide table
        // pushes the whole layout past the viewport.
        '& .RaLayout-contentWithSidebar > *': {
          minWidth: 0,
        },

        // Main content area
        '& .RaLayout-content': {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          marginLeft: '-8px !important',
          paddingLeft: '8px !important',

          ...(!isMobile && {
            height: 'calc(100vh - 44px - 16px)',
            boxSizing: 'border-box',
          }),

          ...(isMobile && {
            padding: (theme as any).custom?.layout?.mobileContainerPadding || '4px',
            paddingTop: (theme as any).custom?.layout?.mobileTopMargin || '16px',
            marginTop: '56px',
          }),
        },

        // Content card
        '& .RaLayout-content > div': {
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          height: '100%',
          backgroundColor: isDashboard
            ? alpha(theme.palette.background.paper, 0.6)
            : theme.palette.background.paper,
          borderRadius: (theme as any).custom?.layout?.card?.borderRadius || '6px',
          boxShadow: (theme as any).custom?.layout?.card?.boxShadow || '0 4px 12px rgba(0, 0, 0, 0.05)',
          overflow: 'auto',
        },
      }}
    >
      {props.children}
    </Layout>
  );
};

export default CustomLayout;
